import { Link } from "@tanstack/react-router";
import { type ReactNode, type MouseEventHandler, useRef, type ElementType } from "react";
import { cn } from "@/lib/utils";

const reduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches &&
  // touch devices: skip magnetic too
  window.matchMedia?.("(pointer: fine)").matches;

const SPARK_PATH =
  "M12 0c.55 6.6 1.45 7.5 12 8-10.55.5-11.45 1.4-12 8-.55-6.6-1.45-7.5-12-8 10.55-.5 11.45-1.4 12-8Z";

function Corners() {
  return (
    <>
      <svg className="spark s-tl" viewBox="0 0 24 24" aria-hidden width={10} height={10}>
        <path d={SPARK_PATH} fill="currentColor" />
      </svg>
      <svg className="spark s-tr" viewBox="0 0 24 24" aria-hidden width={10} height={10}>
        <path d={SPARK_PATH} fill="currentColor" />
      </svg>
      <svg className="spark s-bl" viewBox="0 0 24 24" aria-hidden width={10} height={10}>
        <path d={SPARK_PATH} fill="currentColor" />
      </svg>
    </>
  );
}

/** Magnetic hover on desktop — button subtly follows the cursor within ~8px. */
function useMagnetic() {
  const ref = useRef<HTMLElement>(null);
  if (typeof window === "undefined") return ref as never;

  const onMove: MouseEventHandler<HTMLElement> = (e) => {
    const el = ref.current;
    if (!el || reduced() || window.matchMedia?.("(pointer: coarse)").matches) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left - r.width / 2) * (8 / r.width);
    const y = (e.clientY - r.top - r.height / 2) * (8 / r.height);
    el.style.transform = `translate(${x}px, ${y}px)`;
  };
  const onLeave: MouseEventHandler<HTMLElement> = () => {
    const el = ref.current;
    if (el) el.style.transform = "";
  };
  const onDown: MouseEventHandler<HTMLElement> = () => {
    const el = ref.current;
    if (el) el.style.setProperty("--press", "0.96");
  };
  const onUp: MouseEventHandler<HTMLElement> = () => {
    const el = ref.current;
    if (el) el.style.removeProperty("--press");
  };

  // Attach via closure props merged below.
  (ref as never as { _h: typeof onMove })._h = onMove;
  (ref as never as { _l: typeof onLeave })._l = onLeave;
  (ref as never as { _d: typeof onDown })._d = onDown;
  (ref as never as { _u: typeof onUp })._u = onUp;
  return ref as never;
}

type CommonProps = {
  className?: string;
  variant?: "primary" | "outline" | "solid-white" | "outline-white";
  children: ReactNode;
};

const baseCls =
  "btn-sparkle inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition-[box-shadow,transform] duration-200 active:scale-95";

const variantCls: Record<NonNullable<CommonProps["variant"]>, string> = {
  primary: "bg-primary text-primary-foreground hover:shadow-[0_14px_30px_-12px_var(--primary)]",
  outline: "btn-outline border border-primary bg-transparent text-primary hover:bg-secondary",
  "solid-white": "bg-white text-primary hover:shadow-[0_14px_30px_-12px_rgba(224,33,138,0.4)]",
  "outline-white":
    "btn-outline border border-white/80 bg-transparent text-primary-foreground hover:bg-white/10",
};

/** Button that navigates to the quote form (used by every "Get a Quote" CTA). */
export function QuoteButton({
  onClick,
  className,
  variant = "primary",
  children = "Get a Quote",
  ...rest
}: CommonProps & {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit";
}) {
  const ref = useMagnetic() as unknown as React.RefObject<HTMLButtonElement>;
  const m = (ref as never as { _h?: MouseEventHandler<HTMLElement> })._h;
  const l = (ref as never as { _l?: MouseEventHandler<HTMLElement> })._l;
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onMouseMove={m as MouseEventHandler<HTMLButtonElement>}
      onMouseLeave={l as MouseEventHandler<HTMLButtonElement>}
      className={cn(baseCls, variantCls[variant], className)}
      {...rest}
    >
      <span>{children}</span>
      <Corners />
    </button>
  );
}

/** Anchor-styled button (tel/mailto/internal Link). */
export function LinkButton({
  to,
  href,
  className,
  variant = "primary",
  children,
  target,
  rel,
}: CommonProps & {
  to?: string;
  params?: Record<string, string>;
  href?: string;
  target?: string;
  rel?: string;
}) {
  const cls = cn(baseCls, variantCls[variant], className);

  if (to) {
    return (
      <Link to={to as "/"} className={cls}>
        <span>{children}</span>
        <Corners />
      </Link>
    );
  }
  return (
    <a href={href} className={cls} target={target} rel={rel}>
      <span>{children}</span>
      <Corners />
    </a>
  );
}
